import { useMutation } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { backendService } from "../../services/backend.service";
import { usePerformGlobalRefresh } from "../header/hooks";

type Params = { refreshUponSuccess?: boolean };

export const usePickOpenShift = ({ refreshUponSuccess = false }: Params = {}) => {
	const { t } = useTranslation();
	const { performGlobalRefresh } = usePerformGlobalRefresh({});
	const { showErrorSnackbar } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	return useMutation({
		mutationFn: async ({
			openShiftId,
			nurseId,
			selectedStart,
			selectedEnd,
			departmentId,
			partial,
		}: {
			openShiftId: string;
			departmentId: string;
			/**
			 * The nurse ID of the nurse picking the shift
			 */
			nurseId?: number;
			selectedStart?: DateTime;
			selectedEnd?: DateTime;
			partial?: boolean;
			/**
			 * Bonus the nurse saw on the card when tapping pickup. When set AND `hoursLeftAtPickup`
			 * is non-null (i.e. they grabbed it inside a non-final window) we show a "Congrats" snackbar.
			 */
			bonusAtPickup?: number;
			hoursLeftAtPickup?: number | null;
		}) => {
			track("pick_open_shift_attempt", { openShiftId, departmentId, nurseId });
			const res = await backendService.pickOpenShift({
				departmentId,
				openShiftId,
				overrideNurseId: nurseId,
				acceptedStartTime: selectedStart,
				acceptedEndTime: selectedEnd,
				partial: partial,
			});
			track("pick_open_shift_success", { openShiftId, departmentId, nurseId });
			if (refreshUponSuccess) {
				try {
					await performGlobalRefresh({
						avoidRefreshFor: {
							notificationsData: true,
							nursesData: true,
						},
					});
				} catch (error) {
					showErrorSnackbar(t("error.refresh.after.pick"), error as Error);
				}
			}
			return res;
		},
		throwOnError: false,
		onSuccess: (_data, variables) => {
			// Only celebrate fast pickups (non-final window).
			if (variables.bonusAtPickup != null && variables.hoursLeftAtPickup != null) {
				showSnackbar(t("incentive.fast.pickup.congrats", { amount: variables.bonusAtPickup }), "success");
			}
		},
		onError: (error) => {
			showErrorSnackbar(t("error.pick.open.shift"), {}, error);
		},
	});
};
